import React, { useEffect, useState } from "react";
import { Button, Modal } from "antd";
import * as Sentry from "@sentry/browser";
import { ApiEntryPendingDrift, ApiPaymentDriftChange, ReconRow } from "./types";
import { fmtPrice, formatFriendly } from "./helpers";
import { CHANGE_LABELS, describeChange } from "./paymentDriftChange";
import { request } from "../../helpers/utilities";
import {
  GET_UPLOAD_RERUN_DRIFT,
  TRIGGER_RECONCILIATION,
} from "../../gql/productStatements";
import { useToast } from "./Toast";
import Icon from "./Icon";

interface Props {
  visible: boolean;
  onClose: () => void;
  row: ReconRow | null;
  onRerunScheduled: () => void;
}

const DASH = "—";

const NOTE: React.CSSProperties = {
  fontSize: 12.5,
  color: "var(--ink-2)",
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  padding: "10px 12px",
  display: "flex",
  gap: 8,
  alignItems: "flex-start",
};

function ChangeLine({ change }: { change: ApiPaymentDriftChange }) {
  const { before, after } = describeChange(change);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "120px 190px 1fr",
        gap: 12,
        padding: "8px 0",
        borderTop: "1px solid var(--border)",
        fontSize: 12.5,
        alignItems: "start",
      }}
    >
      <div className="mono">
        {change.paymentTaId
          ? `TA ${change.paymentTaId}`
          : `#${change.paymentId ?? DASH}`}
      </div>
      <div style={{ color: "var(--danger)", fontWeight: 600 }}>
        {CHANGE_LABELS[change.change] ?? change.change}
      </div>
      <div style={{ wordBreak: "break-word" }}>
        <span style={{ color: "var(--muted)" }}>{before}</span>
        <Icon name="chevron-right" size={11} style={{ margin: "0 6px" }} />
        <span style={{ fontWeight: 600 }}>{after}</span>
      </div>
    </div>
  );
}

function DriftedEntry({ entry }: { entry: ApiEntryPendingDrift }) {
  const { pendingDrift: drift } = entry;

  return (
    <div style={{ marginBottom: 26 }}>
      <div
        className="section-label"
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <span>
          {entry.product.name} · {formatFriendly(entry.priceDate)}
        </span>
        <span style={{ color: "var(--muted)", fontWeight: 500 }}>
          {entry.product.fundCode} · {entry.txCount} transactions ·{" "}
          {entry.priceAmount != null ? fmtPrice(entry.priceAmount) : DASH}
        </span>
      </div>

      {drift.price?.changed && (
        <div style={{ ...NOTE, marginBottom: 10 }}>
          <Icon name="alert-triangle" size={13} strokeWidth={2.2} />
          <span>
            <strong style={{ color: "var(--ink-2)" }}>
              Unit price restated
            </strong>{" "}
            from{" "}
            {drift.price.previous != null
              ? fmtPrice(drift.price.previous)
              : DASH}{" "}
            to{" "}
            {drift.price.current != null ? fmtPrice(drift.price.current) : DASH}
            . Every transaction&rsquo;s units on this value date derive from it,
            so the whole sheet needs rerunning.
          </span>
        </div>
      )}

      {!drift.comparable && (
        <div style={NOTE}>
          <Icon name="info" size={13} strokeWidth={2.2} />
          <span>
            This entry reconciled before we began recording which payment each
            transaction matched, so we can&rsquo;t name what moved &mdash; only
            that a payment or price on this product changed since it ran.
          </span>
        </div>
      )}

      {drift.comparable && drift.changes.length === 0 && !drift.price?.changed && (
        <div style={NOTE}>
          <Icon name="info" size={13} strokeWidth={2.2} />
          <span>
            A payment on this product was touched, but nothing that affects this
            reconciliation changed. Rerunning is safe but should not alter the
            result.
          </span>
        </div>
      )}

      {drift.changes.length > 0 && (
        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "120px 190px 1fr",
              gap: 12,
              fontSize: 11.5,
              fontWeight: 600,
              color: "var(--muted)",
              textTransform: "uppercase",
              letterSpacing: 0.4,
              paddingBottom: 6,
            }}
          >
            <div>Payment</div>
            <div>What changed</div>
            <div>Before &rarr; after</div>
          </div>
          {drift.changes.map((change) => (
            <ChangeLine
              key={`${change.paymentId}-${change.change}`}
              change={change}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function RerunOptionsModal({
  visible,
  onClose,
  row,
  onRerunScheduled,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [entries, setEntries] = useState<ApiEntryPendingDrift[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [blockedError, setBlockedError] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    let current = true;

    if (!visible || !row || !row.statementUploadId) return undefined;

    setLoading(true);
    setLoadError(null);
    setBlockedError(null);
    request({
      query: GET_UPLOAD_RERUN_DRIFT,
      variables: { id: row.statementUploadId },
    })
      .then((res: any) => {
        if (!current) return;
        setEntries(res?.data?.data?.statementUpload?.entriesNeedingRerun ?? []);
      })
      .catch((err) => {
        Sentry.captureException(err);
        if (current)
          setLoadError("We couldn’t load what changed on this upload.");
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    // Guards against a slower response for a previously opened upload landing
    // after this one and overwriting its drift list and productIds.
    return () => {
      current = false;
    };
  }, [visible, row]);

  const handleRerun = (changedOnly: boolean) => {
    if (!row || !row.statementUploadId) return;
    setSubmitting(true);
    setBlockedError(null);
    const productIds = changedOnly ? entries.map((e) => e.product.id) : null;
    request({
      query: TRIGGER_RECONCILIATION,
      variables: { uploadId: row.statementUploadId, productIds },
    })
      .then((res: any) => {
        const errors =
          res?.data?.errors || res?.data?.data?.triggerReconciliation?.errors;
        if (errors && errors.length > 0) {
          const first = errors[0];
          // A missing price is a standing block, not a transient failure - it
          // stays wrong until someone restores the price or reverses the
          // upload, so it gets a banner that sticks around rather than a
          // toast that is gone in a few seconds.
          if (first.property === "price") {
            setBlockedError(first.description);
          } else {
            toast.push({
              kind: "error",
              title: "Failed to schedule rerun",
              desc: first.description || first.message,
            });
          }
        } else {
          toast.push({
            kind: "success",
            title: "Reconciliation scheduled",
            desc: changedOnly
              ? `Rerun scheduled for ${entries.length} drifted ${
                  entries.length === 1 ? "product" : "products"
                }.`
              : "Rerun scheduled for the entire upload.",
          });
          onRerunScheduled();
          onClose();
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
        toast.push({
          kind: "error",
          title: "Error scheduling rerun",
          desc: err.message,
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Modal
      visible={visible}
      className="rerun-modal"
      title="Rerun Reconciliation Options"
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="changed"
          type="primary"
          className="bg-petra-blue"
          onClick={() => handleRerun(true)}
          loading={submitting}
          disabled={loading || entries.length === 0}
        >
          {entries.length === 1
            ? "Rerun This Product"
            : `Rerun ${entries.length} Drifted Products`}
        </Button>,
        <Button
          key="all"
          type="primary"
          danger
          onClick={() => handleRerun(false)}
          loading={submitting}
          disabled={loading}
        >
          Rerun Entire Upload
        </Button>,
      ]}
      width={960}
      bodyStyle={{ padding: "28px 32px" }}
    >
      {blockedError && (
        <div
          style={{
            ...NOTE,
            marginBottom: 20,
            borderColor: "var(--danger)",
            background: "var(--danger-bg)",
          }}
        >
          <Icon
            name="alert-circle"
            size={14}
            strokeWidth={2.2}
            style={{ color: "var(--danger)" }}
          />
          <div>
            <div
              style={{
                fontWeight: 700,
                color: "var(--danger)",
                marginBottom: 4,
              }}
            >
              Rerun blocked
            </div>
            <div style={{ color: "var(--ink-2)" }}>{blockedError}</div>
            <div style={{ color: "var(--muted)", marginTop: 6, fontSize: 12 }}>
              A restored price lets this rerun proceed as normal. If the price
              is gone for good, this upload should be reversed instead of rerun
              &mdash; reversal is on the Uploads tab, under this upload&rsquo;s
              record.
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div style={{ color: "var(--muted)", fontSize: 13 }}>
          Working out what changed…
        </div>
      )}

      {loadError && (
        <div style={{ color: "var(--danger)", fontSize: 13 }}>{loadError}</div>
      )}

      {!loading && !loadError && entries.length === 0 && (
        <div style={NOTE}>
          <Icon name="info" size={13} strokeWidth={2.2} />
          <span>
            Nothing on this upload has drifted. Rerunning the whole upload is
            still available, but no reconciliation on it is known to be out of
            date.
          </span>
        </div>
      )}

      {!loading && !loadError && entries.length > 0 && (
        <>
          <p style={{ marginBottom: 22, fontSize: 14, lineHeight: 1.6 }}>
            {entries.length === 1
              ? "One reconciliation on this upload is out of date. "
              : `${entries.length} reconciliations on this upload are out of date. `}
            Everything below has changed in Phoenix since it last ran &mdash;
            the other products on this upload are unaffected.
          </p>
          {entries.map((entry) => (
            <DriftedEntry key={entry.id} entry={entry} />
          ))}
        </>
      )}
    </Modal>
  );
}
